'use client';

import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { dayLabel, shortMonth } from '@/lib/transactions/dates';
import { amountColor } from '@/lib/transactions/utils';
import { cn } from '@/lib/utils';

interface Props {
  date: Date;
  total: number;
  /**
   * `signed` (the transactions list) colors the day by direction and prefixes inflows with "+"; `neutral` is for
   * lists where every amount is spend of the same kind (a space's expenses), so the total reads as a plain sum.
   */
  tone?: 'signed' | 'neutral';
  /** `static` where the header is not over a scrolling list (a single previewed line). */
  className?: string;
}

const WEEKDAYS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'] as const;

export function DayHeader({ date, total, tone = 'signed', className }: Props) {
  const formatCurrency = useFormatCurrency();
  const label = dayLabel(date);
  // `dayLabel` already returns the weekday + date for anything that isn't
  // Hoje / Ontem, so only show the auxiliary `fullDate` for those two cases.
  const isRelative = label === 'Hoje' || label === 'Ontem';
  const fullDate = `${WEEKDAYS[date.getDay()]}, ${date.getDate()} ${shortMonth(date.getMonth())}`;
  const signed = tone === 'signed';
  // A zero day reads muted in either tone.
  const totalClass = amountColor(total, tone, total === 0);

  return (
    <div
      // Not `inert`: the header holds nothing focusable, and it is where a row's date lives — inert would hide the day
      // from assistive technology along with the tab order.
      className={cn(
        'sticky top-0 z-10 flex items-baseline justify-between border-b border-border/60 bg-surface/85 px-5 py-2.5 backdrop-blur-xl',
        className,
      )}
    >
      <div className="flex items-baseline gap-2">
        <h3 className="text-sm font-semibold text-text-primary">{label}</h3>
        {isRelative && (
          <p className="font-mono text-2xs tracking-wider text-text-tertiary uppercase">{fullDate}</p>
        )}
      </div>
      <p className={`font-mono text-sm font-medium tabular-nums ${totalClass}`}>
        {signed && total > 0 ? '+' : ''}{formatCurrency(total)}
      </p>
    </div>
  );
}
